import classNames from 'classnames';
import React from 'react';
import { useLocation } from 'react-router-dom';

import Pagination from 'components/common/paginator';
import NoteAccordionTable from './components/NoteAccordionTable';
import EmployeeNoteItem from 'components/common/employeeNoteItem';
import NoteSummaryFilters from './components/NoteSummaryFilters';

import { MINIMUM_PAGE_COUNT } from 'constants/page';

import useTitle from 'hooks/useTitle';

import { getNoteSummaryColumns } from './noteSummaryColumns';
import { useNoteSummary } from './useNoteSummary';

import type { ExpandedNoteRow, NoteSummaryItem } from './interfaces';

const NoteSummary: React.FC = () => {
  useTitle('Notes Summary');

  const location = useLocation();

  const {
    filters,
    queryParams,
    pageData,
    pageCount,
    columnToSortBy,
    isAscendingOrdered,
    notes,
    isFetchingNotes,
    onPageChange,
    onPageSizeChange,
    onStatusChange,
    onRoleChange,
    onHeaderClick,
  } = useNoteSummary({ pathname: location.pathname, search: location.search });

  const columns = React.useMemo(
    () =>
      getNoteSummaryColumns({
        columnToSortBy,
        isAscendingOrdered,
        onHeaderClick,
      }),
    [columnToSortBy, isAscendingOrdered, onHeaderClick]
  );

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3 className="table-title">Notes Summary</h3>
            </div>
            <NoteSummaryFilters
              filters={filters}
              queryParams={queryParams}
              onStatusChange={onStatusChange}
              onRoleChange={onRoleChange}
            />
          </div>
          <div className={classNames('full-scope-card__content')}>
            <NoteAccordionTable<NoteSummaryItem>
              columns={columns}
              data={notes}
              loading={isFetchingNotes}
              subComponent={({ index, original }: ExpandedNoteRow) => (
                <EmployeeNoteItem key={index} note={original} showHeader={false} />
              )}
              showPagination={false}
              emptyMessage="Notes not found."
            />
            <Pagination
              pageData={pageData}
              pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default NoteSummary;
